import {
  Alert, Box, Button, Chip, CircularProgress, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Tooltip, Typography,
} from '@mui/material';
import { LinkedIn as LinkedInIcon, Facebook as FacebookIcon, LinkOff as LinkOffIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApiCallWithAuth } from '../../hooks/useApiCallWithAuth';
import { disconnectSocialAccount, getFacebookConnectUrl, getLinkedInConnectUrl, listSocialAccounts } from '../../api/social';
import { SocialAccountItem } from '../../types/social';

export function SocialAccountsPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<SocialAccountItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [disconnectingGuid, setDisconnectingGuid] = useState<string | null>(null);

  const { request: listAccountsRequest } = useApiCallWithAuth(listSocialAccounts);
  const { request: disconnectRequest } = useApiCallWithAuth(disconnectSocialAccount);

  function loadAccounts() {
    setLoading(true);
    listAccountsRequest()
      .then((data: SocialAccountItem[]) => setAccounts(data))
      .catch(() => setError('Failed to load connected accounts'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadAccounts(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const connected = searchParams.get('connected');
  const connectError = searchParams.get('error');

  function dismissNotice() {
    const next = new URLSearchParams(searchParams);
    next.delete('connected');
    next.delete('error');
    setSearchParams(next, { replace: true });
  }

  async function handleDisconnect(socialAccountGuid: string) {
    setDisconnectingGuid(socialAccountGuid);
    try {
      await disconnectRequest(socialAccountGuid);
      loadAccounts();
    } catch {
      setError('Failed to disconnect account');
    } finally {
      setDisconnectingGuid(null);
    }
  }

  return (
    <Box>
      {connected && (
        <Alert severity='success' sx={{ mb: 2 }} onClose={dismissNotice}>
          {connected === 'linkedin' ? 'LinkedIn' : 'Facebook'} connected successfully.
        </Alert>
      )}
      {connectError && (
        <Alert severity='error' sx={{ mb: 2 }} onClose={dismissNotice}>
          Failed to connect {connectError === 'linkedin' ? 'LinkedIn' : 'Facebook'}. Please try again.
        </Alert>
      )}

      <Stack direction='row' spacing={1} sx={{ mb: 3 }}>
        <Button
          variant='contained'
          startIcon={<LinkedInIcon />}
          onClick={() => { window.location.href = getLinkedInConnectUrl(); }}
        >
          Connect LinkedIn
        </Button>
        <Button
          variant='contained'
          startIcon={<FacebookIcon />}
          onClick={() => { window.location.href = getFacebookConnectUrl(); }}
        >
          Connect Facebook
        </Button>
      </Stack>

      {loading && <CircularProgress size={24} />}
      {error && <Typography color='error' sx={{ mb: 2 }}>{error}</Typography>}

      {!loading && !error && accounts.length === 0 && (
        <Typography color='text.secondary'>No social accounts connected yet.</Typography>
      )}

      {!loading && accounts.length > 0 && (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell><strong>Platform</strong></TableCell>
              <TableCell><strong>Destination</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Last Connected</strong></TableCell>
              <TableCell align='right'><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {accounts.map(account => (
              <TableRow key={account.socialAccountGuid} hover>
                <TableCell>
                  {account.platform === 'LinkedIn' ? <LinkedInIcon fontSize='small' /> : <FacebookIcon fontSize='small' />}
                </TableCell>
                <TableCell>
                  <Typography variant='body2'>{account.displayName}</Typography>
                </TableCell>
                <TableCell>
                  {!account.isActive ? (
                    <Chip label='Disconnected' size='small' variant='outlined' />
                  ) : account.needsReconnect ? (
                    <Chip label='Needs Reconnect' size='small' color='warning' />
                  ) : (
                    <Chip label='Connected' size='small' color='success' />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant='body2' color='text.secondary'>
                    {account.lastConnectedAt
                      ? new Date(account.lastConnectedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell align='right'>
                  {account.isActive && (
                    <Tooltip title='Disconnect'>
                      <span>
                        <Button
                          size='small'
                          color='error'
                          startIcon={<LinkOffIcon fontSize='small' />}
                          disabled={disconnectingGuid === account.socialAccountGuid}
                          onClick={() => handleDisconnect(account.socialAccountGuid)}
                        >
                          Disconnect
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
